import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import { Address } from "@types"

// =====================
// Type definitions des réponses des services
// =====================

/**
 * Réponse de l'API pour la recherche par SIREN
 */
export type GetCompanyBySiren = Etablissement

/**
 * Etablissement retourné par l'API
 */
export type Etablissement = {
    /**
     * Adresse de l'établissement
     */
    address?: Address
    /**
     * Code APF
     */
    naf_code?: string
    /**
     * Nom
     */
    name?: string
    /**
     * Numéro de SIREN
     */
    siren?: string
    /**
     * Numéro de SIRET
     */
    siret?: string
}

/**
 * Résultat de recherche d'entreprise
 */
export type CompanySearchResult = {
    /**
     * Identifiant MongoDB de l'entreprise (si existante dans Homeys)
     */
    _id?: string
    /**
     * Nom de l'entreprise
     */
    name: string
    /**
     * Type d'organisation (entreprise, asso, copro, collectivite)
     */
    type?: string
    /**
     * Adresse de l'entreprise
     */
    address?: Address
    /**
     * Identifiant de l'entreprise (SIREN, RNA, etc.)
     */
    idcompany?: string
    /**
     * Forme juridique (SARL, SAS, etc.)
     */
    legalForm?: string
}

/**
 * Résultat de recherche INSEE
 */
export type CompanyInseeResult = {
    /**
     * Nom de l'entreprise
     */
    name: string
    /**
     * Numéro SIREN
     */
    siren: string
    /**
     * Numéro SIRET
     */
    siret?: string
    /**
     * Adresse de l'entreprise
     */
    address?: Address
    /**
     * Forme juridique légale
     */
    forme_juridique_legale?: string
}

/**
 * Élément de prévisualisation multi-company (GET /v5/user/:userId/multicompany/preview)
 */
export type CompanyMultipleCompanyPreviewItem = {
    _id: string
    name: string
    img?: {
        crop_image?: string
    }
    iniiial?: string
    role?: string
    disabled?: boolean
    operation?: {
        stripe?: {
            plan?: string
            subscriptions?: Array<{ name?: string; status?: string }>
        }
    }
}

/**
 * Type d'organisation (segment d'URL GET /v5/company/search/:type → idtype MongoDB)
 */
export type CompanySearchOrganizationType =
    | "entreprise"
    | "asso"
    | "copro"
    | "collectivite"

/**
 * Paramètres de recherche d'entreprise
 * (aligné sur GET /v5/company/search/:type?text=…)
 */
export type SearchCompanyParams = {
    /**
     * Type d'organisation (path) — défaut : entreprise
     */
    type?: CompanySearchOrganizationType | string
    /**
     * Texte de recherche (query obligatoire côté API)
     */
    text: string
    /**
     * AbortController pour annuler la requête
     */
    controller?: AbortController
}

// =====================
// Service
// =====================

/**
 * Le service permettant de gérer les organisations
 */
export class CompanyService {
    /**
     * Récupère les informations d'une entreprise par son SIREN
     * @param siren - Le numéro de siren de l'entreprise
     * @param controller - (optionnel) Un AbortController pour annuler la requête si nécessaire
     * @returns Les informations de l'entreprise trouvée
     */
    getCompanyBySiren(siren: string, controller?: AbortController) {
        return handleServiceCall<GetCompanyBySiren>(async () => {
            const {
                data: { etablissements },
            } = await axios.get(`/api/bati/entreprise/information/siren`, {
                params: { siren },
                signal: controller?.signal,
            })
            return etablissements?.[0]
        })
    }

    /**
     * Recherche des entreprises par nom dans les invités Homeys
     * GET /api/metier/v5/company/search/:type?text=
     * @param params - Paramètres de recherche
     * @returns Liste des entreprises correspondantes
     */
    searchCompany(params: SearchCompanyParams) {
        return handleServiceCall<CompanySearchResult[]>(async () => {
            const type = params.type || "entreprise"
            const { data } = await axios.get(
                `/api/metier/v5/company/search/${encodeURIComponent(type)}`,
                {
                    params: { text: params.text },
                    signal: params.controller?.signal,
                }
            )
            return (
                data?.map(
                    (c: {
                        _id?: string
                        name: string
                        type?: string
                        address?: Address
                        idcompany?: string
                        legalForm?: string
                    }) => ({
                        _id: c._id,
                        name: c.name,
                        type: c.type,
                        address: c.address,
                        idcompany: c.idcompany,
                        legalForm: c.legalForm,
                    })
                ) || []
            )
        })
    }

    /**
     * Recherche des entreprises par nom dans la base INSEE (api-bati)
     * GET /api/bati/entreprise/information?name=
     * @param text - Texte de recherche (paramètre query `name` côté bati)
     * @param controller - (optionnel) Annulation de la requête (aligné sur searchCompany)
     * @returns Liste des entreprises INSEE correspondantes
     */
    searchCompanyInsee(text: string, controller?: AbortController) {
        return handleServiceCall<CompanyInseeResult[]>(async () => {
            const {
                data: { etablissements = [] },
            } = await axios.get<{
                etablissements?: Array<{
                    name?: string
                    siren: string
                    siret: string
                    address?: Address
                    forme_juridique_legale?: string
                }>
            }>(`/api/bati/entreprise/information`, {
                params: { name: text },
                signal: controller?.signal,
            })
            return etablissements.map(
                (c): CompanyInseeResult => ({
                    name: c.name ?? "",
                    siren: c.siren,
                    siret: c.siret,
                    address: c.address,
                    forme_juridique_legale: c.forme_juridique_legale,
                })
            )
        })
    }

    /**
     * Récupère la prévisualisation des entreprises d'un utilisateur (multi-company)
     * GET /api/metier/v5/user/:userId/multicompany/preview
     * @param userId - Identifiant de l'utilisateur
     * @returns Liste de prévisualisation des entreprises
     */
    getCompanyMultipleCompanyPreview(
        userId: string
    ): Promise<CompanyMultipleCompanyPreviewItem[]> {
        return handleServiceCall<CompanyMultipleCompanyPreviewItem[]>(
            async () => {
                const { data } = await axios.get<
                    CompanyMultipleCompanyPreviewItem[]
                >(`/api/metier/v5/user/${userId}/multicompany/preview`)
                return Array.isArray(data) ? data : []
            }
        )
    }

    /**
     * Met à jour les informations d'une entreprise
     * PUT /api/metier/v5/company
     * @param payload - Données à mettre à jour (phone, address, representant, idcompany, legalForm)
     * @returns Les données mises à jour
     */
    updateCompany(payload: {
        phone?: string
        address?: Address
        representant?: string
        idcompany?: string
        legalForm?: string
    }) {
        return handleServiceCall<{ success: boolean; phone?: string }>(
            async () => {
                const { data } = await axios.put("/api/metier/company", payload)
                return data
            }
        )
    }

    /**
     * Upload le logo d'une organisation
     * PUT /api/metier/company/image
     * Envoie un FormData avec deux champs fichier attendus par l'API : "raw" et "crop".
     * @param rawFile - Fichier brut (non recadré) de la photo
     * @param cropFile - Fichier recadré de la photo
     * @returns Promise<void>
     */
    uploadCompanyLogo(payload: { rawFile: File; cropFile: File }) {
        return handleServiceCall<void>(async () => {
            const formData = new FormData()
            formData.append("raw", payload.rawFile, payload.rawFile.name)
            formData.append(
                "crop",
                payload.cropFile,
                payload.cropFile.name || "company-logo.png"
            )
            await axios.put("/api/metier/company/image", formData, {
                headers: {
                    "Content-Type": undefined,
                } as Record<string, string | undefined>,
            })
        })
    }

    /**
     * Récupère les sources de facturation d'une organisation
     * GET /api/metier/company/billing/source
     * @param params - Paramètres de pagination et filtres
     * @param params.page - Numéro de la page
     * @param params.limit - Nombre d'éléments par page
     * @param params.search - Recherche textuelle
     * @param params.count - Retourne uniquement le nombre total de sources
     * @param params.status - Filtre sur les sources (active, removed, '')
     * @param params.date - Filtre sur les dates (current_month, not_current_month, '')
     * @param params.idtype - Filtre sur le type de source (all, pdl, pce, etc.)
     * @param params.criteres - Filtres du bandeau (DEV-621, M2) :
     *   `sur_le_compte`, `ajoutee_periode`, `maj_periode`, `facturee`
     *   (`oui` / `non`), `si_supprimee` (`libere` / `reste`) ; omis s'ils sont vides
     * @param params.compteurs - demande `compteurs` (`compteurs=oui`) même sans filtre
     * @returns Liste des sources de facturation (avec `compteurs` par filtre
     *   quand l'API les sert) ou nombre total si count=true
     */
    getBillingSource(
        params: {
            page?: number
            limit?: number
            search?: string | null
            count?: boolean
            status?: "" | "active" | "removed"
            date?: "" | "current_month" | "not_current_month"
            idtype?: string
            criteres?: Record<string, string>
            compteurs?: boolean
        } = {}
    ) {
        return handleServiceCall<
            { data: any[]; has_more: boolean; compteurs?: unknown } | number
        >(async () => {
            const {
                page = 1,
                limit = 20,
                search = null,
                count = false,
                status = "",
                date = "",
                idtype = "all",
                criteres = {},
                compteurs = false,
            } = params

            const { data } = await axios.get(
                "/api/metier/company/billing/source",
                {
                    params: {
                        page,
                        limit,
                        search,
                        count,
                        status,
                        date,
                        idtype: idtype === "all" ? null : idtype,
                        ...criteres,
                        // `compteurs=oui` : nombres par filtre et `filtrees`
                        // servis même sans filtre (liste de l'écran)
                        ...(compteurs && !count ? { compteurs: "oui" } : {}),
                    },
                }
            )

            // Si le paramètre count est à true, on renvoie uniquement le nombre total
            if (count) return data?.total ?? 0

            return data ?? { data: [], has_more: false }
        })
    }

    /**
     * Nombre de sources facturées et plafond du forfait servi (DEV-2254)
     * GET /api/metier/company/billing/source/count
     * @returns `total` et `plafond` (bloc brut, `null` sans forfait lisible ;
     *   absent tant que l'API ne le sert pas)
     */
    getBillingSourceCount() {
        return handleServiceCall<{ total?: number; plafond?: unknown }>(
            async () => {
                const { data } = await axios.get(
                    "/api/metier/company/billing/source/count"
                )
                // Réponse vide : aucun nombre (une absence n'est jamais un 0, DEV-621)
                return data ?? {}
            }
        )
    }

    /**
     * Télécharge l'export des sources de facturation
     * GET /api/metier/company/billing/export
     * @returns URL de téléchargement de l'export
     */
    downloadExportSourceBilling() {
        return handleServiceCall<{ status: string; result: { url: string } }>(
            async () => {
                const { data } = await axios.get(
                    "/api/metier/company/billing/export"
                )
                return {
                    status: data?.url ? "success" : "failure",
                    result: {
                        url: data?.url,
                    },
                }
            }
        )
    }

    /**
     * Récupère les factures
     * GET /api/metier/company/invoices
     * @param controller - (optionnel) Un AbortController pour annuler la requête si nécessaire
     * @returns Liste des factures avec informations de pagination
     */
    getInvoices(controller?: AbortController) {
        return handleServiceCall<
            { nextPage?: number; total?: number; data: any[] } | any[]
        >(async () => {
            const { data } = await axios.get("/api/metier/company/invoices", {
                signal: controller?.signal,
            })
            return data
        })
    }

    /**
     * Télécharge toutes les factures d'une organisation
     * POST /api/metier/company/invoices/download
     *
     * DEV-1088 — on demande le ZIP **binaire en flux** (`Accept: application/zip`)
     * plutôt que l'ancien base64, qui bufferisait l'archive entière des deux
     * côtés et gonflait le corps de ~33 %.
     *
     * La réponse reste néanmoins de type incertain, et c'est voulu :
     * api-metier et front-v6 se déploient séparément, donc cette requête peut
     * tomber sur une version d'api-metier qui ne connaît pas la négociation et
     * rend du base64. `Blob` = régime binaire, `string` = régime historique ;
     * l'appelant doit gérer les deux (cf. `versBlobZip` dans InvoiceCard).
     *
     * @param controller - (optionnel) Un AbortController pour annuler la requête si nécessaire
     * @returns L'archive ZIP (Blob) ou son base64 (string) selon la version d'api-metier
     */
    downloadInvoicesAsArchive(controller?: AbortController) {
        return handleServiceCall<Blob | string>(async () => {
            const { data } = await axios.post(
                "/api/metier/company/invoices/download",
                {},
                {
                    headers: { Accept: "application/zip" },
                    responseType: "blob",
                    signal: controller?.signal,
                }
            )
            return data
        })
    }
}
